// What a move costs while it is dragged (issue 1151; design/30 §1.3): the board works out the route's cost by the
// table's distance rule over the squares of difficult terrain THIS screen holds — the DM's zones, or a player's
// `fog.difficult` (her explored map's only) — and the readout over the board says it. A preview: the server counts the
// move again at commit, over every square, and stamps that on the move.
import { OBJECT_LIMITS, cutCorners, difficultSpans, moveLabel, moveOfLabel, routeCost, spanTest, type WallLike, type DistanceRules, type Grid, type PlayerFog, type Point, type ProjectedState, type Size, type Span, type Zone } from '@dragons-table/vtt-shared';
import { capRoute } from './route';

/** The squares of difficult terrain this screen holds: the DM's zones, else what the player was sent. */
export function heldDifficult(state: Pick<ProjectedState, 'zones' | 'fog'>): Span[] {
  if (state.zones) return difficultSpans(state.zones as Zone[]);
  return (state.fog as PlayerFog | undefined)?.difficult ?? [];
}

/** A cell test for the ruler, or undefined when this screen holds no difficult terrain. */
export function difficultTest(state: Pick<ProjectedState, 'zones' | 'fog'>): ((c: { i: number; j: number }) => boolean) | undefined {
  const spans = heldDifficult(state);
  return spans.length ? spanTest(spans) : undefined;
}

/**
 * A route as the server will count it (qa-runs#1433): capped as the drop sends it, then the side squares the hand
 * clipped on the way to a diagonal cut, by the walls this screen holds (the server cuts again with every wall).
 */
export function countedRoute(start: Point, route: readonly Point[], size: Size, grid: Grid, extent: { width: number; height: number }, walls: readonly WallLike[]): Point[] {
  return cutCorners(start, capRoute([...route], OBJECT_LIMITS.route), size, grid, extent, walls);
}

/**
 * The readout's words for a route so far: "30 ft (10 ft difficult ×2)", or null when nothing is counted. With `reach`
 * (the kind of move picked, qa-runs 1263) it reads as the confirmation will: "Move: 45 of 30 ft (15 ft past its limit)".
 */
export function dragCostLabel(start: Point, route: readonly Point[], size: Size, grid: Grid, extent: { width: number; height: number }, distance: DistanceRules | null, difficult: readonly Span[], reach?: { label: string; budget: number } | null, counted?: { walls: readonly WallLike[]; diagonals?: number | undefined }): string | null {
  const cost = routeCost(start, counted ? countedRoute(start, route, size, grid, extent, counted.walls) : route, size, grid, extent, distance, difficult, counted?.diagonals ?? 0);
  if (!cost || cost.squares <= 0) return null;
  const words = moveLabel(cost, distance);
  if (!reach || !distance) return words;
  return `${moveOfLabel(reach.label, cost.squares, reach.budget, distance)}${words.includes('(') ? ` · ${words}` : ''}`;
}

type Listener = (text: string | null) => void;
let text: string | null = null;
const listeners = new Set<Listener>();
/** The readout's store: the canvas sets it during a drag and clears it on release. */
export const dragReadout = {
  get: (): string | null => text,
  set(next: string | null): void { if (next === text) return; text = next; for (const l of listeners) l(text); },
  subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
};
